import { createAsyncThunk } from '@reduxjs/toolkit'
import { Actions } from '@state/actions'
import {
  ExamDto,
  ExamService,
  SiteDto,
  SiteService,
  StatsServices,
  TableStatsPrescriberDti,
  TableStatsUserDti,
  convertedDataForCharts,
} from '@services/api'
import {
  fetchExamStatsCount,
  fetchExamTypeCodeBreakdown,
  StatsExamTypeCodeBreakdownDTO,
} from '@services/examStatsService'
import {
  fetchPatientAgeSummary,
  fetchPatientCareTypeBreakdown,
  fetchPatientGenderBreakdown,
  fetchPatientOriginByDepartments,
  fetchPatientOriginByMunicipalities,
  fetchPatientStatsCount,
  StatsPatientAgeSummaryDTO,
  StatsPatientCareTypeBreakdownDTO,
  StatsPatientGenderBreakdownDTO,
  StatsPatientOriginByDepartmentDTO,
  StatsPatientOriginByMunicipalityDTO,
} from '@services/patientStatsService'
import {
  fetchReferringPhysicianDepartmentsBreakdown,
  fetchReferringPhysicianPostalCodesBreakdown,
  fetchReferringPhysicianSpecialtiesBreakdown,
  fetchReferringPhysicianStatsCount,
  StatsReferringPhysicianDepartmentBreakdownDTO,
  StatsReferringPhysicianPostalCodeBreakdownDTO,
  StatsReferringPhysicianSpecialtyBreakdownDTO,
} from '@services/referringPhysicianStatsService'
import { PaginatedDto } from '@services/extendedType'

const getThunkErrorMessage = (error: any) => {
  if (error?.response?.data?.message) {
    return error.response.data.message
  }

  return error?.message ?? 'Impossible de charger les statistiques'
}

export const getAllPrescriber = createAsyncThunk(
  Actions.STATS_GET_ALL_PRESCRIBER,
  async (_, { rejectWithValue, getState }) => {
    try {
      const resultGetState:any = getState()
      const { startY, endY, siteId } = resultGetState.stats.filter
      console.log('resultGetState', startY, endY, siteId)
      const result: TableStatsPrescriberDti[] = await StatsServices.fetchAllPrescriber({ startYear: startY, endYear: endY, siteId })
      return result
    } catch (e: any) {
      if (e.response) {
        return rejectWithValue(e.response.data.message)
      }
      return rejectWithValue(e.message)
    }
  }
)

// commence ici tu dois faire en sorte de mettre des state directe dans les thunks

export const getExamBreakdown = createAsyncThunk(
  Actions.STATS_GET_EXAM_BREACKDOWN,
  async (_, { rejectWithValue, getState }) => {
    try {
      const resultGetState:any = getState()
      const { siteId } = resultGetState.stats.filter
      const result = await StatsServices.fetchAllExamBreakdown({siteId: siteId})
      return convertedDataForCharts(result)
    } catch (e: any) {
      if (e.response) {
        return rejectWithValue(e.response.data.message)
      }
      return rejectWithValue(e.message)
    }
  }
)

export const getAllTopEvent = createAsyncThunk(
  Actions.STATS_GET_TOP_EVENT,
  async (_, { rejectWithValue, getState }) => {
    try {
      const resultGetState:any = getState()
      const { accountType, eventType, siteId } = resultGetState.stats.filter
      const result: TableStatsUserDti[] = await StatsServices.fetchAllTopEvent({ accountType: accountType, eventType: eventType, siteId: siteId })
      return result
    } catch (e: any) {
      if (e.response) {
        return rejectWithValue(e.response.data.message)
      }
      return rejectWithValue(e.message)
    }
  }
)

export const getExamStatsOptions = createAsyncThunk<
  { sites: SiteDto[]; exams: ExamDto[] },
  void,
  { rejectValue: string }
>(Actions.STATS_GET_EXAM_OPTIONS, async (_, { rejectWithValue }) => {
  try {
    const [sitesResponse, examsResponse] = await Promise.all([
      SiteService.findAll(),
      ExamService.findAll({ limit: 1000, page: 1 }),
    ])

    return {
      sites: (sitesResponse as SiteDto[]).sort((left, right) =>
        left.label.localeCompare(right.label),
      ),
      exams: ((examsResponse as PaginatedDto<ExamDto>)?.datas ?? []).sort(
        (left, right) => left.label.localeCompare(right.label),
      ),
    }
  } catch (error: any) {
    return rejectWithValue(getThunkErrorMessage(error))
  }
})

export const getExamStats = createAsyncThunk<
  {
    total: number
    breakdown: StatsExamTypeCodeBreakdownDTO[]
  },
  void,
  { rejectValue: string }
>(Actions.STATS_GET_EXAM_STATS, async (_, { rejectWithValue, getState }) => {
  try {
    const state = getState() as {
      stats: {
        examStats: {
          filters: {
            siteId?: number
            examId?: number
            startDate: string
            endDate: string
          }
        }
      }
    }
    const { filters } = state.stats.examStats

    if (filters.startDate > filters.endDate) {
      return rejectWithValue(
        'La date de début doit être antérieure à la date de fin',
      )
    }

    const [countResponse, breakdownResponse] = await Promise.all([
      fetchExamStatsCount(filters),
      fetchExamTypeCodeBreakdown(filters),
    ])

    return {
      total: countResponse.total,
      breakdown: breakdownResponse,
    }
  } catch (error: any) {
    return rejectWithValue(getThunkErrorMessage(error))
  }
})

export const getPatientStatsOptions = createAsyncThunk<
  { sites: SiteDto[] },
  void,
  { rejectValue: string }
>(Actions.STATS_GET_PATIENT_OPTIONS, async (_, { rejectWithValue }) => {
  try {
    const sitesResponse = await SiteService.findAll()

    return {
      sites: (sitesResponse as SiteDto[]).sort((left, right) =>
        left.label.localeCompare(right.label),
      ),
    }
  } catch (error: any) {
    return rejectWithValue(getThunkErrorMessage(error))
  }
})

export const getPatientStats = createAsyncThunk<
  {
    total: number
    departments: StatsPatientOriginByDepartmentDTO[]
    municipalities: StatsPatientOriginByMunicipalityDTO[]
    careTypes: StatsPatientCareTypeBreakdownDTO[]
    genders: StatsPatientGenderBreakdownDTO[]
    ageSummary: StatsPatientAgeSummaryDTO
  },
  void,
  { rejectValue: string }
>(Actions.STATS_GET_PATIENT_STATS, async (_, { rejectWithValue, getState }) => {
  try {
    const state = getState() as {
      stats: {
        patientStats: {
          filters: {
            siteId?: number
            departmentCode?: string
            zipCode?: string
            startDate: string
            endDate: string
          }
        }
      }
    }
    const { filters } = state.stats.patientStats

    if (filters.startDate > filters.endDate) {
      return rejectWithValue(
        'La date de début doit être antérieure à la date de fin',
      )
    }

    const [
      countResponse,
      departmentResponse,
      municipalityResponse,
      careTypeResponse,
      genderResponse,
      ageSummaryResponse,
    ] = await Promise.all([
      fetchPatientStatsCount(filters),
      fetchPatientOriginByDepartments(filters),
      fetchPatientOriginByMunicipalities(filters),
      fetchPatientCareTypeBreakdown(filters),
      fetchPatientGenderBreakdown(filters),
      fetchPatientAgeSummary(filters),
    ])

    return {
      total: countResponse.total,
      departments: departmentResponse,
      municipalities: municipalityResponse,
      careTypes: careTypeResponse,
      genders: genderResponse,
      ageSummary: ageSummaryResponse,
    }
  } catch (error: any) {
    return rejectWithValue(getThunkErrorMessage(error))
  }
})

export const getReferringPhysicianStatsOptions = createAsyncThunk<
  { sites: SiteDto[] },
  void,
  { rejectValue: string }
>(
  Actions.STATS_GET_REFERRING_PHYSICIAN_OPTIONS,
  async (_, { rejectWithValue }) => {
    try {
      const sitesResponse = await SiteService.findAll()

      return {
        sites: (sitesResponse as SiteDto[]).sort((left, right) =>
          left.label.localeCompare(right.label),
        ),
      }
    } catch (error: any) {
      return rejectWithValue(getThunkErrorMessage(error))
    }
  },
)

export const getReferringPhysicianStats = createAsyncThunk<
  {
    total: number
    departments: StatsReferringPhysicianDepartmentBreakdownDTO[]
    postalCodes: StatsReferringPhysicianPostalCodeBreakdownDTO[]
    specialties: StatsReferringPhysicianSpecialtyBreakdownDTO[]
  },
  void,
  { rejectValue: string }
>(
  Actions.STATS_GET_REFERRING_PHYSICIAN_STATS,
  async (_, { rejectWithValue, getState }) => {
    try {
      const state = getState() as {
        stats: {
          referringPhysicianStats: {
            filters: {
              siteId?: number
              departmentCode?: string
              zipCode?: string
            }
          }
        }
      }
      const { filters } = state.stats.referringPhysicianStats

      const [
        countResponse,
        departmentResponse,
        postalCodeResponse,
        specialtyResponse,
      ] = await Promise.all([
        fetchReferringPhysicianStatsCount(filters),
        fetchReferringPhysicianDepartmentsBreakdown(filters),
        fetchReferringPhysicianPostalCodesBreakdown(filters),
        fetchReferringPhysicianSpecialtiesBreakdown(filters),
      ])

      return {
        total: countResponse.total,
        departments: departmentResponse,
        postalCodes: postalCodeResponse,
        specialties: specialtyResponse,
      }
    } catch (error: any) {
      return rejectWithValue(getThunkErrorMessage(error))
    }
  },
)
